/**
 * ── A DATE THAT HAS ALREADY PASSED IS NOT A REQUEST (2026-08-27) ───────────────
 *
 * Layne, from the Review Board feedback: "there's 3 records of feedback around being
 * able to select dates that have passed." Three separate divisions filed the same
 * complaint, so this is the form accepting something no one can act on: a Target Due
 * Date or a Target Deployment Date in the past files a job that is late the moment it
 * lands, and Creative Traffic has to chase the requester to find out what they meant.
 *
 * Both entry points let it through, for different reasons:
 *   · A NEW job — neither date input carried a `min`, so the picker happily offered
 *     last month and a typed value was never checked.
 *   · A DUPLICATED job — worse, and the likelier source of all three reports. Duplicate
 *     copies a PAST job by design ("PAST job means deployed", dupJobs.pastOnly), so its
 *     deploy stamp and due date are past BY CONSTRUCTION. The copy arrived pre-filled
 *     with them and looked like a filled-in form, which is exactly the shape a
 *     requester clicks through without re-reading.
 *
 * Everything here is pure and takes `now` so the tests do not depend on the clock.
 * Dates are the `<input type="date">` wire format (YYYY-MM-DD) and are compared as
 * LOCAL calendar days, never parsed with `new Date("2026-08-27")` — that reads as UTC
 * midnight and makes "today" look past for anyone west of Greenwich, which would have
 * traded one wrong rejection for another.
 */

/** today as YYYY-MM-DD, in the browser's own timezone */
export function todayISO(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/** true when `iso` names a calendar day BEFORE today. Blank / malformed = not past:
 *  an empty required field is already caught by the completeness gate, and inventing a
 *  second complaint about it would bury the real one. */
export function isPastDate(iso: unknown, now: Date = new Date()): boolean {
  const v = String(iso == null ? "" : iso).trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  return v < todayISO(now);          /* ISO days sort lexicographically — no parsing */
}

/**
 * true when date+time is in the past, INCLUDING a time earlier today. "Target
 * Deployment Date/Time" is one field with two inputs, and today at 08:00 requested at
 * 12:40 is the same defect Layne reported — the date passes and the send still can't
 * happen. A missing time is treated as the end of the day, so a bare future date is
 * never called past on the strength of a time nobody entered.
 */
export function isPastDateTime(date: unknown, time: unknown, now: Date = new Date()): boolean {
  const d = String(date == null ? "" : date).trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return false;
  if (isPastDate(d, now)) return true;
  if (d !== todayISO(now)) return false;
  const t = String(time == null ? "" : time).trim();
  const m = /^(\d{1,2}):(\d{2})$/.exec(t);
  if (!m) return false;
  const mins = parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  return mins < now.getHours() * 60 + now.getMinutes();
}

/** the value to prefill with: keep a usable date, drop one that has passed. Blank is
 *  the honest state for a copied job — the requester picks the new date deliberately
 *  instead of inheriting the old one and clicking past it. */
export function dropPastDate(iso: unknown, now: Date = new Date()): string {
  const v = String(iso == null ? "" : iso).trim();
  return isPastDate(v, now) ? "" : v;
}

/** what the form says when it refuses. One sentence, names the field, says what to do. */
export const PAST_DATE_MSG: Record<string, string> = {
  targetDueDate: "Target Due Date has already passed — pick today or a later date.",
  deployDate: "Target Deployment Date/Time has already passed — pick a date and time still ahead.",
};
